import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import type { CourseWithRelations } from '@/lib/types';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

export function useCoursesAll() {
	return useQuery<CourseWithRelations[]>({
		queryKey: ['get-courses-all', schoolId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('courses')
				.select(
					`
					course_name,
					course_id,
					course_division,
					is_saved_draft,
					is_archived,
					submission:submissions!courses_submission_id_fkey (
						admin_approval,
						dept_approval
					),
					courses_disciplines (
						discipline:disciplines (
							name
						)
					),
					courses_divisions (
						division:divisions (
							name
						)
					),
					courses_users (
						user_id,
						course_id,
						user:users (
							user_id,
							user_first,
							user_last,
							user_dept_chair
						)
					)
				`
				)
				.eq('school_id', schoolId)
				.order('course_name', { ascending: true });

			if (error) throw error;

			// Transform the data to match the expected shape
			const transformedData = data?.map((course: any) => ({
				...course,
				// Rename courses_disciplines to courses_discipline (singular)
				courses_discipline: course.courses_disciplines ?? null,
				// Rename courses_divisions to courses_division (singular)
				courses_division: course.courses_divisions ?? null,
				// Transform courses_users to include dept_chair object structure
				courses_users: course.courses_users?.map((cu: any) => ({
					...cu,
					user: cu.user
						? {
								...cu.user,
								// Transform user_dept_chair string to dept_chair object
								dept_chair: cu.user.user_dept_chair ? { user_id: cu.user.user_dept_chair } : null,
							}
						: null,
				})),
			}));

			return transformedData as CourseWithRelations[];
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}
